import { useEffect, useState } from 'react';
import {
  BrowserLocalRecordingRecovery,
  BrowserLocalRecordingRecoveryRestoreResult,
  BrowserLocalRecordingType,
  defaultBrowserLocalRecordingRecovery,
} from '../utils/browserLocalRecordingRecovery';

type AvailableBrowserLocalRecordingRecovery = Extract<
  BrowserLocalRecordingRecoveryRestoreResult,
  { status: 'available' }
>;

interface UseBrowserLocalRecordingRecoveryPromptOptions {
  conversationId?: string;
  recordingType: BrowserLocalRecordingType;
  unavailableMessage: string;
  inspectErrorMessage: string;
  recordingRecovery?: BrowserLocalRecordingRecovery;
}

export interface BrowserLocalRecordingRecoveryPromptState {
  recoverableRecording: AvailableBrowserLocalRecordingRecovery | null;
  // True until the local store has been read. Callers must not render the
  // recorder before then, or a pending recording appears as a late swap.
  isInspectingRecovery: boolean;
  recoveryWarning: string | null;
  restoreRecoveredRecording: () => AvailableBrowserLocalRecordingRecovery | null;
  discardRecoveredRecording: () => Promise<void>;
}

export const useBrowserLocalRecordingRecoveryPrompt = ({
  conversationId,
  recordingType,
  unavailableMessage,
  inspectErrorMessage,
  recordingRecovery = defaultBrowserLocalRecordingRecovery,
}: UseBrowserLocalRecordingRecoveryPromptOptions): BrowserLocalRecordingRecoveryPromptState => {
  const [recoverableRecording, setRecoverableRecording] = useState<AvailableBrowserLocalRecordingRecovery | null>(null);
  const [recoveryWarning, setRecoveryWarning] = useState<string | null>(null);
  const [isInspectingRecovery, setIsInspectingRecovery] = useState(Boolean(conversationId));

  useEffect(() => {
    if (!conversationId) {
      setIsInspectingRecovery(false);
      return;
    }

    setIsInspectingRecovery(true);

    let isMounted = true;
    recordingRecovery.restore(conversationId, recordingType)
      .then((result) => {
        if (!isMounted) return;
        setIsInspectingRecovery(false);

        if (result.status === 'available') {
          setRecoverableRecording(result);
          setRecoveryWarning(null);
          return;
        }

        if (result.reason !== 'missing') {
          setRecoveryWarning(unavailableMessage);
        }
      })
      .catch((error) => {
        console.warn(inspectErrorMessage, error);
        if (isMounted) {
          setIsInspectingRecovery(false);
          setRecoveryWarning(unavailableMessage);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [conversationId, inspectErrorMessage, recordingRecovery, recordingType, unavailableMessage]);

  const restoreRecoveredRecording = (): AvailableBrowserLocalRecordingRecovery | null => {
    if (!recoverableRecording) return null;

    setRecoverableRecording(null);
    return recoverableRecording;
  };

  const discardRecoveredRecording = async (): Promise<void> => {
    if (!conversationId) return;

    await recordingRecovery.discard(conversationId, recordingType);
    setRecoverableRecording(null);
  };

  return {
    recoverableRecording,
    isInspectingRecovery,
    recoveryWarning,
    restoreRecoveredRecording,
    discardRecoveredRecording,
  };
};
